import { getEnv } from "@istari-frontend/shared";

/**
 * Report an error to Datadog RUM.
 *
 * Safe to call from the main bundle at any time: before the SDK loads, calls
 * are buffered (bounded) and flushed once `initRum()` completes; when RUM is
 * off they become no-ops and the buffer is discarded. The SDK itself is never
 * imported statically, so builds with RUM disabled ship none of it.
 */
export function addRumError(error: unknown, context?: RumErrorContext): void {
  if (reporter) {
    reporter(error, context);
    return;
  }
  if (pendingErrors && pendingErrors.length < MAX_BUFFERED_ERRORS) {
    pendingErrors.push([error, context]);
  }
}

/**
 * Synchronous "will RUM take over" decision, shared by `initRum()` and the
 * Sentry bootstrap in main.tsx so the two cannot diverge: Sentry hands off
 * tracing/replay only when RUM will actually initialize (flag on, not under
 * Playwright, credentials present). The one residual gap — the SDK chunk
 * failing to load after this returns true — costs that session tracing/replay
 * only; Sentry error capture is unaffected.
 */
export function rumEligible(): boolean {
  if (getEnv("VITE_DATADOG_RUM_ENABLED") !== "true") return false;
  if (navigator.webdriver) return false;
  return REQUIRED_ENV_KEYS.every((key) => Boolean(getEnv(key)));
}

/**
 * Initialize Datadog Browser RUM for the current page.
 *
 * Gated by `VITE_DATADOG_RUM_ENABLED`, which deployment config sets
 * per-environment; builds where it is unset or "false" never fetch the SDK —
 * the dynamic `import()` isolates the Datadog code into its own JS chunk.
 *
 * Also skipped under Playwright (`navigator.webdriver`) so it doesn't conflict
 * with the test-driven trace propagation in the e2e suite.
 */
export async function initRum(): Promise<void> {
  if (getEnv("VITE_DATADOG_RUM_ENABLED") !== "true" || navigator.webdriver) {
    pendingErrors = null;
    return;
  }

  const applicationId = getEnv("VITE_DATADOG_APPLICATION_ID");
  const clientToken = getEnv("VITE_DATADOG_CLIENT_TOKEN");
  const site = getEnv("VITE_DATADOG_SITE");
  const service = getEnv("VITE_DATADOG_SERVICE");
  const env = getEnv("VITE_DATADOG_ENV");
  const version = getEnv("VITE_RELEASE_VERSION");

  if (!applicationId || !clientToken || !site || !service || !env || !version) {
    pendingErrors = null;
    const missing = REQUIRED_ENV_KEYS.filter((key) => !getEnv(key)).join(", ");
    throw new Error(`RUM enabled but missing required env vars: ${missing}`);
  }

  const { datadogRum } = await import("@datadog/browser-rum");

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: replaySampleRate(),
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: privacyLevel(),
    // Action names are derived from clicked-element text, which puts real
    // page content (system names, file names) into plain RUM events
    // regardless of the replay privacy level — always mask them.
    enablePrivacyForActionName: true,
    traceContextInjection: "sampled",
    // Replay every session that hits an error. Datadog cannot record
    // retroactively (no Sentry-style error buffer), so force-starting on the
    // first error captures the session from that point onward when it wasn't
    // in the sampled baseline.
    beforeSend: (event) => {
      if (event.type === "error") {
        datadogRum.startSessionReplayRecording({ force: true });
      }
      return true;
    },
    allowedTracingUrls: [
      (url: string) => {
        try {
          const host = new URL(url, window.location.origin).hostname;
          // Trace istari backend services; skip any Zitadel auth host so OIDC
          // flows don't get tangled in app traces.
          return host.endsWith(".istari.app") && !host.split(".").includes("zitadel");
        } catch {
          return false;
        }
      },
    ],
  });

  // Same identity source Sentry uses in main.tsx, so the two SDKs agree on
  // who a session belongs to. Read at boot like Sentry: a page loaded
  // logged-out stays anonymous until the next load after sign-in.
  const user = readStoredUser();
  if (user) {
    datadogRum.setUser(user);
  }

  // Wire the reporter only once the SDK is initialized, then drain anything
  // captured during boot (React hooks install synchronously; this does not).
  reporter = (error, context) => datadogRum.addError(error, context);
  pendingErrors?.forEach(([error, context]) => reporter?.(error, context));
  pendingErrors = null;
}

// Optional, unlike REQUIRED_ENV_KEYS: unset or malformed means replay off,
// never a failed init. Datadog rejects out-of-range rates, so clamp via
// validation rather than passing through.
//
// Reads window.env directly instead of getEnv(): nginx always injects the
// key at runtime (empty string when the container env is unset), and an
// empty runtime value must mean replay OFF — but getEnv() skips falsy
// window.env entries and would fall back to a build-time import.meta.env
// value, silently re-enabling replay. getEnv() is only the fallback for
// contexts where the key was never runtime-injected (local dev, tests).
// Session Replay redaction level. Defaults to full "mask" (all text redacted)
// so an environment must opt DOWN to readable replays, never the reverse.
// Same runtime-first resolution as replaySampleRate(): an nginx-injected
// empty string means "unset -> mask", and must not fall back to a build-time
// value via getEnv()'s falsy-skip.
function privacyLevel(): "allow" | "mask" | "mask-user-input" {
  const runtimeEnv = (window as { env?: Record<string, string | undefined> }).env;
  const raw =
    runtimeEnv && "VITE_DATADOG_PRIVACY_LEVEL" in runtimeEnv
      ? runtimeEnv.VITE_DATADOG_PRIVACY_LEVEL
      : getEnv("VITE_DATADOG_PRIVACY_LEVEL");
  return raw === "allow" || raw === "mask-user-input" ? raw : "mask";
}

function replaySampleRate(): number {
  const runtimeEnv = (window as { env?: Record<string, string | undefined> }).env;
  const raw =
    runtimeEnv && "VITE_DATADOG_REPLAY_SAMPLE_RATE" in runtimeEnv
      ? runtimeEnv.VITE_DATADOG_REPLAY_SAMPLE_RATE
      : getEnv("VITE_DATADOG_REPLAY_SAMPLE_RATE");
  const rate = Number(raw);
  return Number.isFinite(rate) && rate >= 0 && rate <= 100 ? rate : 0;
}

type RumErrorContext = Record<string, unknown>;
type RumErrorReporter = (error: unknown, context?: RumErrorContext) => void;

const REQUIRED_ENV_KEYS = [
  "VITE_DATADOG_APPLICATION_ID",
  "VITE_DATADOG_CLIENT_TOKEN",
  "VITE_DATADOG_SITE",
  "VITE_DATADOG_SERVICE",
  "VITE_DATADOG_ENV",
  "VITE_RELEASE_VERSION",
];

const MAX_BUFFERED_ERRORS = 20;

let reporter: RumErrorReporter | null = null;
let pendingErrors: Array<[unknown, RumErrorContext | undefined]> | null = [];

type StoredUserProfile = {
  profile?: { sub?: string; email?: string; name?: string; preferred_username?: string };
};

function readStoredUser(): { id: string; email?: string; name?: string } | null {
  try {
    const raw = window.localStorage.getItem("istari-user");
    if (!raw) return null;
    const { profile } = JSON.parse(raw) as StoredUserProfile;
    if (!profile?.sub) return null;
    return { id: profile.sub, email: profile.email, name: profile.name ?? profile.preferred_username };
  } catch {
    return null;
  }
}
